import React from "react";
import "./Container.css";

export enum _flexDirection {
    row = "row",
    column = "column",
    rowReverse = "row-reverse",
    columnReverse = "column-reverse"
}

export enum _flexWrap {
    nowrap = "nowrap",
    wrap = "wrap",
    wrapReverse = "wrap-reverse"
}

export enum _justifyContent {
    flexStart = "flex-start",
    flexEnd = "flex-end",
    center = "center",
    spaceBetween = "space-between",
    spaceAround = "space-around",
    spaceEvenly = "space-evenly"
}

export enum _alignItems {
    stretch = "stretch",
    flexStart = "flex-start",
    flexEnd = "flex-end",
    center = "center",
    baseline = "baseline"
}

export enum _alignContent {
    stretch = "stretch",
    flexStart = "flex-start",
    flexEnd = "flex-end",
    center = "center",
    spaceBetween = "space-between",
    spaceAround = "space-around"
}

export enum _gap {
    none = "0",
    xs = "4px",
    s = "8px",
    m12 = "12px",
    m = "16px",
    l = "24px",
    xl = "32px",
    xxl = "48px"
}

interface ContainerProps {
    // Основные настройки flex
    direction?: _flexDirection;
    wrap?: _flexWrap;
    justify?: _justifyContent;
    align?: _alignItems;
    alignContent?: _alignContent;
    gap?: _gap;

    // Размеры и отступы
    width?: string | number;
    height?: string | number;
    minWidth?: string | number;
    maxWidth?: string | number;
    minHeight?: string | number;
    maxHeight?: string | number;

    // Отступы
    padding?: string | number;
    paddingTop?: string | number;
    paddingRight?: string | number;
    paddingBottom?: string | number;
    paddingLeft?: string | number;

    margin?: string | number;
    marginTop?: string | number;
    marginRight?: string | number;
    marginBottom?: string | number;
    marginLeft?: string | number;

    // Дополнительные стили
    className?: string;
    style?: React.CSSProperties;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;

    // Дети
    children?: React.ReactNode;
}

export const Container: React.FC<ContainerProps> = ({
    // Flex настройки
    direction = _flexDirection.row,
    wrap = _flexWrap.nowrap,
    justify = _justifyContent.flexStart,
    align = _alignItems.flexStart,
    alignContent = _alignContent.flexStart,
    gap = _gap.none,

    // Размеры
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,

    // Отступы
    padding,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,

    margin,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,

    // Дополнительные стили
    className = "",
    style = {},
    onClick,

    // Дети
    children,

    ...props
}) => {
    const containerStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: direction,
        flexWrap: wrap,
        justifyContent: justify,
        alignItems: align,
        alignContent: alignContent,
        gap: gap,

        // Размеры
        ...(width && { width }),
        ...(height && { height }),
        ...(minWidth && { minWidth }),
        ...(maxWidth && { maxWidth }),
        ...(minHeight && { minHeight }),
        ...(maxHeight && { maxHeight }),

        // Отступы
        ...(padding && { padding }),
        ...(paddingTop && { paddingTop }),
        ...(paddingRight && { paddingRight }),
        ...(paddingBottom && { paddingBottom }),
        ...(paddingLeft && { paddingLeft }),

        // Внешние отступы
        ...(margin && { margin }),
        ...(marginTop && { marginTop }),
        ...(marginRight && { marginRight }),
        ...(marginBottom && { marginBottom }),
        ...(marginLeft && { marginLeft }),

        ...style,
    };

    const containerClassName = `container-div ${className}`.trim();

    return (
        <div
            className={containerClassName}
            style={containerStyle}
            onClick={onClick}
            {...props}
        >
            {children}
        </div>
    );
};

// Экспорт типов для удобства использования
export type { ContainerProps };
